import { ApiError } from "@khazzin/api-client";
import { describeApiError } from "@khazzin/api-client/errors";
import type { Translate } from "@khazzin/i18n";
import {
	ArrowDown,
	ArrowUp,
	ArrowUpDown,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	Columns3,
} from "lucide-react";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Button } from "../../primitives/Button";
import { Popover } from "../../primitives/Menu";
import { Checkbox } from "../../primitives/Selection";
import { SkeletonTable } from "../../primitives/Skeleton";
import { cx, FOCUS_RING, TONE_CLASSES, type Tone } from "../../styles";
import {
	type Column,
	defaultHidden,
	readHiddenColumns,
	visibleColumns,
	writeHiddenColumns,
} from "./columns";

/**
 * One table, so a new entity list is configuration rather than a component.
 *
 * Six things here are decisions rather than markup, and each of them is the
 * kind that gets rebuilt slightly differently on the fourth screen if it is not
 * settled once.
 *
 * **Sorting is the server's, always.** A header click changes a query
 * parameter. Sorting the fifty rows on screen would sort a page rather than a
 * list, so "highest price first" would mean "the highest of these fifty" —
 * which is a wrong answer that looks like a right one, and the only kind of
 * sorting bug nobody reports because the numbers on screen really are in order.
 *
 * **Selection is page-scoped.** The header checkbox selects what is visible and
 * says so; there is no "select all 39,989", because the next thing somebody
 * would do with it is a bulk action on rows they have never seen.
 *
 * **The hidden columns are stored, not the visible ones.** A stored visible
 * list freezes the table at the moment somebody last opened the picker, and a
 * column added in a later release is invisible to them forever.
 *
 * **The sticky actions column inherits the row's background.** That is the
 * notch the handoff README warns about: give it its own surface colour and a
 * tinted row shows a white rectangle at its end.
 *
 * **Cards below 640px, by attribute rather than by JavaScript.** The rules live
 * in `index.css` keyed off `data-card-table`, so there is no resize listener to
 * be wrong on the first paint and no second render tree to keep in step.
 * `collapse: "scroll"` opts out, for a hierarchical grid whose meaning is in
 * the columns beside each other.
 *
 * **Every cell carries `data-label`.** It is what the card layout renders as
 * the field name, and it is the translated header — so the mobile view is
 * translated for free rather than by a second set of strings.
 *
 * **The table never pushes the page sideways (ADJ-M03-1).** It scrolls inside
 * its own container whatever `collapse` says: before, only `scroll` tables had
 * one, so a fourteen-column list ran past the viewport and took its actions
 * menu with it. The header is sticky inside that container, a text cell is cut
 * with an ellipsis at 24rem (the whole text is its `title`), and the actions
 * cell is sticky at the inline end and stays on the phone card.
 */

export type { Column } from "./columns";

export interface SortState {
	by: string;
	dir: "asc" | "desc";
}

/**
 * What the footer draws.
 *
 * The first seven fields are every list's. The last four are the Orders page's
 * (`07/07` §3.5) and are optional, because most screens do not need them and a
 * rows-per-page control on a table of eleven rows is furniture.
 *
 * **There is no Last.** A cursor points one way: the server can hand out the
 * token for the next page and cannot name the token for the four-hundredth.
 * Numbered buttons therefore reach only the pages already walked — which is
 * the whole history that can exist without an offset scan of the table.
 */
export interface PaginationProps {
	showingFrom: number;
	showingTo: number;
	total?: number | undefined;
	hasNext: boolean;
	hasPrevious: boolean;
	onNext: () => void;
	onPrevious: () => void;
	/** The 1-based page number, for the numbered buttons and "Page 3 of 8". */
	page?: number;
	/** Back to page one. */
	onFirst?: () => void;
	/** Jump back to a page already walked. Never forward. */
	onPage?: (page: number) => void;
	/** Rows per page, and the sizes offered. Both, or neither. */
	pageSize?: number;
	pageSizes?: readonly number[];
	onPageSize?: (size: number) => void;
}

export interface DataTableProps<Row> {
	/** Persists the column choice under `khazzin:columns:<screenKey>`. */
	screenKey: string;
	columns: Column<Row>[];
	rowKey: (row: Row) => string;
	page: { items: Row[]; nextCursor: string | null } | undefined;
	status: "loading" | "error" | "success";
	error?: ApiError | Error | null;
	onRetry: () => void;
	pagination: PaginationProps;
	sort?: { state: SortState; onChange: (next: SortState) => void };
	selection?: {
		selected: Set<string>;
		onChange: (next: Set<string>) => void;
		bulkActions: ReactNode;
	};
	/**
	 * Opens the record a row stands for.
	 *
	 * A pointer convenience, not the only way in. A row is not a focusable
	 * element and giving every one a tab stop would put fifty of them between
	 * the toolbar and the footer — so a table with this set must also render a
	 * link in one of its cells, which is what a keyboard and a screen reader
	 * actually navigate by.
	 */
	onRowClick?: (row: Row) => void;
	/** Tints a row — an overdue order, a variance. Uses the same tones as a chip. */
	rowTone?: (row: Row) => Tone | undefined;
	/** The sticky end column. */
	rowActions?: (row: Row) => ReactNode;
	toolbar?: ReactNode;
	/** What to render when the page is empty. */
	empty: ReactNode;
	collapse?: "cards" | "scroll";
	/** The smallest width the columns need before the table scrolls. */
	minWidth?: number;
	/**
	 * Group header rows (EP15-S08's list). The rows arrive sorted group by
	 * group from the server; a header row opens each group, spanning the
	 * table, with whatever rollup `header` draws.
	 */
	groups?: { keyOf: (row: Row) => string; header: (key: string) => ReactNode };
	/** Translates the header keys and the table's own strings. */
	t: Translate;
	/** Names the table for assistive technology. */
	caption: string;
}

export function DataTable<Row>({
	screenKey,
	columns,
	rowKey,
	page,
	status,
	error,
	onRetry,
	pagination,
	sort,
	selection,
	onRowClick,
	rowTone,
	rowActions,
	toolbar,
	empty,
	collapse = "cards",
	minWidth = 860,
	groups,
	t,
	caption,
}: DataTableProps<Row>) {
	const [hidden, setHidden] = useState(() => {
		const stored = readHiddenColumns(screenKey);
		return stored.size > 0 ? stored : defaultHidden(columns);
	});
	// Whether this browser has ever had an opinion, which is what decides
	// between "apply defaultHidden" and "apply what they chose".
	const [touched, setTouched] = useState(() => readHiddenColumns(screenKey).size > 0);

	const shown = useMemo(() => visibleColumns(columns, hidden, touched), [columns, hidden, touched]);
	const rows = page?.items ?? [];

	const toggleColumn = useCallback(
		(id: string) => {
			setHidden((current) => {
				const next = new Set(current);
				if (next.has(id)) {
					next.delete(id);
				} else {
					next.add(id);
				}
				writeHiddenColumns(screenKey, next);
				return next;
			});
			setTouched(true);
		},
		[screenKey],
	);

	// The row somebody last clicked, for a Shift-range. One per table rather
	// than one per row: an earlier draft kept it inside the row component, so
	// every row believed the anchor was itself and a range from row 1 to row 3
	// selected rows 0 to 3.
	const anchor = useRef(0);

	const resetColumns = useCallback(() => {
		const back = defaultHidden(columns);
		setHidden(back);
		writeHiddenColumns(screenKey, back);
		setTouched(false);
	}, [columns, screenKey]);

	return (
		// `overflow-clip` rounds the corners without making a scroll container,
		// so the selection bar's `sticky` still follows the page.
		<div className="flex min-w-0 flex-col overflow-clip card">
			{(toolbar !== undefined || columns.some((c) => c.hideable === true)) && (
				<div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
					<div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{toolbar}</div>
					<ColumnPicker
						columns={columns}
						hidden={hidden}
						onToggle={toggleColumn}
						onReset={resetColumns}
						t={t}
					/>
				</div>
			)}

			{selection !== undefined && selection.selected.size > 0 && (
				<SelectionBar selection={selection} t={t} />
			)}

			{status === "loading" && (
				<div role="status" aria-busy="true" aria-label={t("ui.table.loading")} className="p-3">
					<SkeletonTable
						rows={6}
						columns={shown.length}
						selectable={selection !== undefined}
						className="border-0"
					/>
				</div>
			)}

			{status === "error" && (
				<div role="alert" className="flex flex-col items-center gap-3 p-8 text-center">
					<p className="text-[13px] text-fg-muted">
						{/* The server's error in words, never its raw English
						    `message`; a caller's own Error is already words. */}
						{error instanceof ApiError
							? t(describeApiError(error.code))
							: error instanceof Error
								? error.message
								: t("ui.state.errorTitle")}
					</p>
					<Button variant="secondary" size="sm" onClick={onRetry}>
						{t("ui.actions.retry")}
					</Button>
				</div>
			)}

			{status === "success" && rows.length === 0 && <div className="p-8">{empty}</div>}

			{status === "success" && rows.length > 0 && (
				<div
					data-scroll="1"
					// Always its own scroll container (ADJ-M03-1), and capped to the
					// viewport from 640px so the sticky header has something to stick
					// in. Not on a phone (cards), and not on paper.
					className={cx(
						"overflow-x-auto",
						"sm:max-h-[max(24rem,calc(100dvh-10rem))] sm:overflow-y-auto",
						"print:max-h-none print:overflow-visible",
					)}
				>
					<table
						// The attribute is the whole mobile implementation: the rules in
						// index.css turn rows into cards below 640px, so there is no
						// resize listener to be wrong on the first paint.
						{...(collapse === "cards" ? { "data-card-table": "1" } : {})}
						style={{ minWidth }}
						// `separate` with no spacing rather than `collapse`: a collapsed
						// border belongs to the grid and scrolls away from a sticky cell.
						className="w-full border-separate border-spacing-0 text-[length:var(--tbl-fs)]"
					>
						<caption className="sr-only">{caption}</caption>
						<thead>
							<tr className="bg-bg-subtle">
								{selection !== undefined && (
									<th
										scope="col"
										className="sticky top-0 z-[1] h-9 w-[38px] border-b border-border bg-bg-subtle ps-3 text-start"
									>
										<HeaderCheckbox rows={rows} rowKey={rowKey} selection={selection} t={t} />
									</th>
								)}
								{shown.map((column) => (
									<HeaderCell key={column.id} column={column} sort={sort} t={t} />
								))}
								{rowActions !== undefined && (
									<th
										scope="col"
										className="sticky end-0 top-0 z-[2] h-9 w-px border-s border-b border-border bg-bg-subtle"
									>
										<span className="sr-only">{t("ui.table.actions")}</span>
									</th>
								)}
							</tr>
						</thead>
						<tbody className="[&>tr:last-child>*]:border-b-0">
							{rows.map((row, index) => [
								groups !== undefined &&
									(index === 0 || groups.keyOf(rows[index - 1] as Row) !== groups.keyOf(row)) && (
										<tr key={`group:${groups.keyOf(row)}`} className="bg-bg-subtle">
											<th
												scope="rowgroup"
												colSpan={
													shown.length +
													(selection === undefined ? 0 : 1) +
													(rowActions === undefined ? 0 : 1)
												}
												className="h-[30px] border-b border-border px-3 text-start text-[12.5px] font-semibold text-fg"
											>
												{groups.header(groups.keyOf(row))}
											</th>
										</tr>
									),
								<BodyRow
									key={rowKey(row)}
									row={row}
									index={index}
									rows={rows}
									rowKey={rowKey}
									columns={shown}
									anchor={anchor}
									selection={selection}
									{...(onRowClick === undefined ? {} : { onRowClick })}
									{...(rowTone === undefined ? {} : { rowTone })}
									{...(rowActions === undefined ? {} : { rowActions })}
									t={t}
								/>,
							])}
						</tbody>
					</table>
				</div>
			)}

			<Footer pagination={pagination} t={t} />
		</div>
	);
}

// ── header ──────────────────────────────────────────────────────────────────

function HeaderCell<Row>({
	column,
	sort,
	t,
}: {
	column: Column<Row>;
	sort: DataTableProps<Row>["sort"];
	t: Translate;
}) {
	const label = t(column.headerKey);
	const active = sort?.state.by === column.id;
	const sortable = column.sortable === true && sort !== undefined;

	const base = cx(
		"sticky top-0 z-[1] h-9 whitespace-nowrap border-b border-border bg-bg-subtle px-3",
		"text-[12px] font-semibold text-fg-muted",
		column.align === "end" ? "text-end" : "text-start",
	);

	if (!sortable) {
		return (
			<th scope="col" style={{ width: column.width, minWidth: column.minWidth }} className={base}>
				{label}
			</th>
		);
	}

	return (
		<th
			scope="col"
			style={{ width: column.width, minWidth: column.minWidth }}
			className={cx(base, "p-0")}
			// What a screen reader announces about the column's current order, and
			// what a sorted table owes anybody who cannot see the arrow.
			aria-sort={active ? (sort.state.dir === "asc" ? "ascending" : "descending") : "none"}
		>
			<button
				type="button"
				onClick={() =>
					sort.onChange({
						by: column.id,
						// Clicking the column already sorted flips it; clicking a new one
						// starts ascending, which is what every table does.
						dir: active && sort.state.dir === "asc" ? "desc" : "asc",
					})
				}
				className={cx(
					"flex h-9 w-full cursor-pointer items-center gap-1 px-3 text-inherit",
					column.align === "end" ? "justify-end" : "justify-start",
					"hover:text-fg",
					FOCUS_RING,
				)}
			>
				{label}
				<Icon
					// Not mirrored: up and down mean the same in both directions, and a
					// flipped arrow would point at nothing.
					as={active ? (sort.state.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown}
					size={13}
					className={active ? "text-fg" : "text-fg-subtle"}
				/>
			</button>
		</th>
	);
}

function HeaderCheckbox<Row>({
	rows,
	rowKey,
	selection,
	t,
}: {
	rows: Row[];
	rowKey: (row: Row) => string;
	selection: NonNullable<DataTableProps<Row>["selection"]>;
	t: Translate;
}) {
	const keys = rows.map(rowKey);
	const onPage = keys.filter((key) => selection.selected.has(key)).length;
	const state = onPage === 0 ? false : onPage === keys.length ? true : "indeterminate";

	return (
		<Checkbox
			checked={state}
			// Page scope, said out loud. There is no "select all 39,989": the next
			// thing somebody would do with it is a bulk action on rows they have
			// never seen.
			aria-label={t("ui.table.selectPage")}
			onCheckedChange={() => {
				const next = new Set(selection.selected);
				if (state === true) {
					for (const key of keys) {
						next.delete(key);
					}
				} else {
					for (const key of keys) {
						next.add(key);
					}
				}
				selection.onChange(next);
			}}
		/>
	);
}

// ── body ────────────────────────────────────────────────────────────────────

function BodyRow<Row>({
	row,
	index,
	rows,
	rowKey,
	columns,
	anchor,
	selection,
	onRowClick,
	rowTone,
	rowActions,
	t,
}: {
	row: Row;
	index: number;
	rows: Row[];
	rowKey: (row: Row) => string;
	columns: Column<Row>[];
	/** The table's shared Shift anchor. */
	anchor: React.RefObject<number>;
	selection?: DataTableProps<Row>["selection"];
	onRowClick?: (row: Row) => void;
	rowTone?: (row: Row) => Tone | undefined;
	rowActions?: (row: Row) => ReactNode;
	t: Translate;
}) {
	const key = rowKey(row);
	const selected = selection?.selected.has(key) ?? false;
	const tone = rowTone?.(row);

	// Whether Shift was down for the press being handled.
	const shift = useRef(false);

	function toggle(shiftHeld: boolean) {
		if (selection === undefined) {
			return;
		}
		const next = new Set(selection.selected);
		// Shift takes everything between the last click and this one, which is
		// how anybody who has used a file manager expects to select forty rows.
		const from = shiftHeld ? Math.min(anchor.current, index) : index;
		const to = shiftHeld ? Math.max(anchor.current, index) : index;

		for (let i = from; i <= to; i++) {
			const target = rows[i];
			if (target === undefined) {
				continue;
			}
			const id = rowKey(target);
			if (selected) {
				next.delete(id);
			} else {
				next.add(id);
			}
		}
		anchor.current = index;
		selection.onChange(next);
	}

	return (
		<tr
			// `aria-selected` rather than a class alone: a selected row is a state a
			// screen reader has a word for.
			aria-selected={selection === undefined ? undefined : selected}
			onClick={
				onRowClick === undefined
					? undefined
					: (event) => {
							// Not when the press landed in a cell that owns its own
							// controls — the checkbox, the actions menu. Nor when it landed
							// outside the row's own DOM: a dialog a cell opened is portaled
							// out, and React still bubbles its clicks through the row.
							if (
								event.currentTarget.contains(event.target as Node) &&
								(event.target as HTMLElement).closest("[data-row-stop]") === null
							) {
								onRowClick(row);
							}
						}
			}
			className={cx(
				// Each cell draws the row's rule: `border-separate` has no row borders.
				"[&>td]:border-b [&>td]:border-border",
				// The tint is on the row, and the sticky cell below inherits it. One
				// background only: two background utilities on one element are won
				// by stylesheet order, not class order, so "selected" could lose.
				selected
					? "bg-brand-subtle"
					: tone === undefined
						? "bg-bg-surface hover:bg-bg-subtle"
						: TONE_CLASSES[tone],
				onRowClick !== undefined && "cursor-pointer",
			)}
			style={{ height: "var(--row-h)" }}
		>
			{selection !== undefined && (
				// `data-row-stop` rather than a stopPropagation handler: the row's
				// own click checks for it, so nothing static here carries an event
				// listener and the checkbox stays the only interactive thing in the
				// cell. Without the guard, selecting a row would also open it.
				<td data-row-stop="1" className="w-[38px] ps-3">
					<Checkbox
						checked={selected}
						aria-label={t("ui.table.selectRow")}
						// The modifier comes off the real click rather than a document
						// listener guessing at it. This runs before onCheckedChange.
						onClick={(event) => {
							shift.current = event.shiftKey;
						}}
						onCheckedChange={() => toggle(shift.current)}
					/>
				</td>
			)}

			{columns.map((column) => (
				<td
					key={column.id}
					// The card layout renders this as the field name. It is the
					// translated header, so the mobile view is translated for free.
					data-label={t(column.headerKey)}
					{...(column.card === "wide" ? { "data-card-wide": "1" } : {})}
					{...(column.card === "hide" ? { "data-card-hide": "1" } : {})}
					className={cx(
						"px-3",
						column.align === "end" &&
							"whitespace-nowrap text-end [font-variant-numeric:tabular-nums]",
						// An identifier is read whole: a SKU broken at its hyphen reads as
						// two codes.
						column.mono === true && "whitespace-nowrap font-mono",
					)}
				>
					<CellContent value={column.cell(row)} />
				</td>
			))}

			{rowActions !== undefined && (
				<td
					// `bg-inherit` is the fix for the notch the handoff README warns
					// about: a sticky cell with its own surface colour shows a white
					// rectangle at the end of every tinted or selected row.
					className="sticky end-0 z-[1] w-px whitespace-nowrap border-s border-border bg-inherit px-2 text-end"
					data-row-stop="1"
					// Kept on the phone card as its footer (index.css): a row whose
					// actions vanish on a phone is a row nobody can act on there.
					data-row-actions="1"
				>
					{rowActions(row)}
				</td>
			)}
		</tr>
	);
}

/**
 * A cell's value. Plain text is cut to one line with an ellipsis and carries
 * the whole of itself as `title`, so a long name cannot widen its column past
 * 24rem; anything richer is the column's own layout and is left alone.
 */
function CellContent({ value }: { value: ReactNode }) {
	if (typeof value !== "string" && typeof value !== "number") {
		return <>{value}</>;
	}
	return (
		<span className="block max-w-96 truncate" title={String(value)}>
			{value}
		</span>
	);
}

// ── chrome ──────────────────────────────────────────────────────────────────

function SelectionBar<Row>({
	selection,
	t,
}: {
	selection: NonNullable<DataTableProps<Row>["selection"]>;
	t: Translate;
}) {
	return (
		<div className="sticky top-0 flex flex-wrap items-center gap-3 border-b border-border bg-bg-subtle px-3 py-2">
			{/* The count goes through ICU, so Arabic gets its six plural forms
			    rather than "3 محدد" for every number. */}
			<span className="text-[12.5px] text-fg [font-variant-numeric:tabular-nums]">
				{t("ui.table.selected", { count: selection.selected.size })}
			</span>
			<Button size="sm" variant="ghost" onClick={() => selection.onChange(new Set())}>
				{t("ui.actions.clear")}
			</Button>
			<span className="flex-1" />
			{selection.bulkActions}
		</div>
	);
}

function ColumnPicker<Row>({
	columns,
	hidden,
	onToggle,
	onReset,
	t,
}: {
	columns: Column<Row>[];
	hidden: Set<string>;
	onToggle: (id: string) => void;
	onReset: () => void;
	t: Translate;
}) {
	const hideable = columns.filter((column) => column.hideable === true);

	if (hideable.length === 0) {
		return null;
	}

	// A popover rather than a DropdownMenu: a menu closes when an item is
	// chosen and moves with arrows rather than Tab, and a person turning four
	// columns off would have to reopen it four times. Portalled (ADJ-M03-1), so
	// no scroll container can clip it.
	return (
		<Popover
			align="end"
			className="flex w-56 flex-col gap-1 p-2"
			trigger={
				<Button size="sm" variant="secondary" icon={Columns3}>
					{t("ui.table.columns")}
				</Button>
			}
		>
			{hideable.map((column) => (
				<Checkbox
					key={column.id}
					checked={!hidden.has(column.id)}
					onCheckedChange={() => onToggle(column.id)}
					label={t(column.headerKey)}
				/>
			))}
			<Button size="sm" variant="ghost" onClick={onReset} className="mt-1 justify-start">
				{t("ui.actions.clear")}
			</Button>
		</Popover>
	);
}

function Footer({ pagination, t }: { pagination: PaginationProps; t: Translate }) {
	const {
		showingFrom,
		showingTo,
		total,
		hasNext,
		hasPrevious,
		onNext,
		onPrevious,
		page,
		onFirst,
		onPage,
		pageSize,
		pageSizes,
		onPageSize,
	} = pagination;

	return (
		<div className="flex flex-wrap items-center gap-3 border-t border-border p-3">
			<span
				// `status`, so a screen reader hears the new range after paging
				// rather than having to go looking for it.
				role="status"
				className="text-[12.5px] text-fg-muted [font-variant-numeric:tabular-nums]"
			>
				{/* Two messages rather than one with an optional half: most endpoints
				    here have no total — counting is a second query over the same
				    index — and "1–50 of undefined" is worse than "1–50". */}
				{total === undefined
					? t("ui.table.range", { from: showingFrom, to: showingTo })
					: t("ui.table.rangeOf", { from: showingFrom, to: showingTo, total })}
			</span>

			{pageSize !== undefined && pageSizes !== undefined && onPageSize !== undefined && (
				<label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
					{t("ui.table.rowsPerPage")}
					<select
						value={pageSize}
						onChange={(event) => onPageSize(Number(event.target.value))}
						className={cx(
							"h-7 rounded-md border border-border bg-bg-surface px-1.5 text-[12.5px] text-fg",
							FOCUS_RING,
						)}
					>
						{pageSizes.map((size) => (
							<option key={size} value={size}>
								{size}
							</option>
						))}
					</select>
				</label>
			)}

			<span className="flex-1" />

			{onFirst !== undefined && (
				<Button
					size="sm"
					variant="secondary"
					icon={ChevronsLeft}
					mirrorIcons
					disabled={!hasPrevious}
					onClick={onFirst}
				>
					{t("ui.table.first")}
				</Button>
			)}
			<Button
				size="sm"
				variant="secondary"
				icon={ChevronLeft}
				mirrorIcons
				disabled={!hasPrevious}
				onClick={onPrevious}
			>
				{t("ui.actions.previous")}
			</Button>
			{page !== undefined && onPage !== undefined && (
				<PageNumbers page={page} onPage={onPage} t={t} />
			)}
			<Button
				size="sm"
				variant="secondary"
				iconEnd={ChevronRight}
				mirrorIcons
				disabled={!hasNext}
				onClick={onNext}
			>
				{t("ui.actions.next")}
			</Button>
		</div>
	);
}

/** How many numbered buttons fit beside the arrows before they crowd a phone. */
const PAGE_BUTTONS = 5;

/**
 * The pages already walked, as buttons.
 *
 * Only backwards, and that is the contract rather than a shortcut: reaching an
 * unvisited page needs its cursor, and the only way to have one is to have been
 * there. The last few are shown, because the pages somebody wants to go back to
 * are the ones they just came from.
 */
function PageNumbers({
	page,
	onPage,
	t,
}: {
	page: number;
	onPage: (page: number) => void;
	t: Translate;
}) {
	const first = Math.max(1, page - PAGE_BUTTONS + 1);
	const numbers: number[] = [];
	for (let n = first; n <= page; n++) {
		numbers.push(n);
	}

	return (
		<div className="flex items-center gap-1" data-card-hide="1">
			{numbers.map((n) => (
				<Button
					key={n}
					size="sm"
					variant={n === page ? "primary" : "secondary"}
					aria-current={n === page ? "page" : undefined}
					aria-label={t("ui.table.page", { page: n })}
					disabled={n === page}
					onClick={() => onPage(n)}
					className="min-w-8 [font-variant-numeric:tabular-nums]"
				>
					{n}
				</Button>
			))}
		</div>
	);
}
